import type { ReactNode } from 'react';
import { Time } from '@gonfalon/datetime/Time';
import { DateFormat } from '@gonfalon/format/constants';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import styles from './epochAnnotations.module.css';

/**
 * Matches 13-digit integers, which are millisecond epochs between 2001-09-09 and 2286-11-20.
 *
 * Seconds-based (10-digit) epochs are deliberately excluded: they collide with unrelated flag
 * JSON values such as `rolloutSeed` and `_version`, and LaunchDarkly stores date clause values,
 * expiring targets, and modification timestamps in milliseconds.
 *
 * Quoted and negative values are excluded as well. Targeting clause values are JSON strings, so a
 * 13-digit user or segment ID would otherwise be presented to an approval reviewer as a date.
 */
const EPOCH_MS_PATTERN = /(?<!["\d.-])\d{13}(?!["\d.])/g;

/**
 * Formatted with the time zone: an approval reviewer is often in a different zone than the
 * requester, so an unlabeled wall-clock time is ambiguous exactly where a date-targeting mistake
 * would hide. Mirrors the zone-labeled treatment of unix-millisecond timestamps in
 * `ClauseReadOnlyView` and `ScheduledChangesCard`, which render date-clause epochs through the
 * same `Time` component. `notooltip` because this already *is* the tooltip.
 */
function LocalTime({ epochMs }: { epochMs: number }) {
  return <Time datetime={epochMs} dateFormat={DateFormat.EEEE_MMMM_D_YYY_H_MM_A_Z} includeTimeZone notooltip />;
}

/**
 * Splits `text` on millisecond epoch values, wrapping each one so it renders the equivalent
 * local time in a tooltip. Returns the original text untouched when it contains no epoch values.
 */
export function annotateEpochTimestamps(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(EPOCH_MS_PATTERN)) {
    const value = match[0];
    const start = match.index;

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    nodes.push(
      <TooltipTrigger key={`epoch-${keyPrefix}-${start}`}>
        <Focusable>
          {/*
           * `role="button"` is required, not incidental: a tooltip has to be keyboard reachable, and
           * `Focusable` rejects a child without an interactive role. The tooltip is wired up as the
           * element's accessible description, so the value is announced along with its local time.
           */}
          <span role="button" data-epoch-timestamp className={styles.epochTimestamp}>
            {value}
          </span>
        </Focusable>
        <Tooltip>
          <LocalTime epochMs={Number(value)} />
        </Tooltip>
      </TooltipTrigger>,
    );

    lastIndex = start + value.length;
  }

  if (nodes.length === 0) {
    return [text];
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}
